import { create } from 'zustand';
import { useAuthStore } from './authStore';
import { CalendarEvent, EventMember, RecurringPattern, DateType, Priority } from '../types/calendar';
import { getBaseEventId } from '../utils/recurringInstance';

interface FetchedDateRange {
  startDate: string;
  endDate: string;
  timestamp: number;
}

interface EventState {
  events: CalendarEvent[];
  fetchedRanges: FetchedDateRange[]; // Track which date ranges have been fetched
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: string | null; // eventId being deleted
  error: string | null;
  fetchEvents: (startDate?: string, endDate?: string) => Promise<void>;
  clearEvents: () => void; // Clear all events (for logout, etc.)
  createEvent: (event: {
    title: string;
    description?: string;
    startTime: Date;
    endTime: Date;
    memberIds: string[];
    location?: string;
    category?: string;
    isRecurring?: boolean;
    recurringPattern?: RecurringPattern | string;
    dateType?: DateType;
    priority?: Priority;
    reminderDays?: number[];
    isImportantDate?: boolean;
    isAllDay?: boolean;
  }) => Promise<CalendarEvent>;
  updateEvent: (eventId: string, updates: {
    title?: string;
    description?: string;
    startTime?: Date;
    endTime?: Date;
    memberIds?: string[];
    location?: string;
    category?: string;
    isRecurring?: boolean;
    recurringPattern?: RecurringPattern | string;
    dateType?: DateType;
    priority?: Priority;
    reminderDays?: number[];
    isImportantDate?: boolean;
    isAllDay?: boolean;
    updateAllInstances?: boolean;
  }) => Promise<void>;
  deleteEvent: (eventId: string, instanceOnly?: boolean) => Promise<void>;
  clearError: () => void;
  // Important dates filters and queries
  getImportantDates: () => CalendarEvent[];
  getByDateType: (dateType: DateType) => CalendarEvent[];
  getByPriority: (priority: Priority) => CalendarEvent[];
  getUpcomingImportantDates: (days: number) => CalendarEvent[];
}

import { getApiUrl, getApiKey } from '../utils/apiConfig';

// Get API configuration (throws if not configured)
const API_URL = getApiUrl();
const API_KEY = getApiKey();

// Helper to get headers with auth token and API key
const getHeaders = (): HeadersInit => {
  const { idToken, isFamilyMode } = useAuthStore.getState();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (API_KEY) {
    headers['X-Api-Key'] = API_KEY;
  }
  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }
  if (isFamilyMode) {
    headers['X-Family-Mode'] = 'true';
    headers['X-Attribution-Actor'] = 'family';
  }
  return headers;
};

/**
 * Convert backend Event to frontend CalendarEvent
 * 
 * TIMEZONE HANDLING:
 * - Backend sends startTime/endTime in one of two formats:
 *   1. Full ISO 8601 UTC strings for timed events (e.g., "2024-01-15T19:00:00.000Z")
 *   2. Date-only strings for all-day events (e.g., "2024-01-15")
 * - For full ISO strings: new Date(isoString) automatically parses UTC and converts to local timezone
 * - For date-only strings: new Date("YYYY-MM-DD") creates a Date object at midnight in local timezone
 * - Date objects in the store represent the moment in time, displayed in user's local timezone
 * - All display components should use time formatting utilities from utils/time.ts
 */
const convertBackendEventToCalendarEvent = (backendEvent: any): CalendarEvent => {
  // Support both legacy (single member) and new (multiple members) format
  const members: EventMember[] = backendEvent.members && backendEvent.members.length > 0
    ? backendEvent.members.map((m: any) => ({
        memberId: m.memberId,
        memberName: m.memberName,
        memberColor: m.memberColor,
        memberAvatar: m.memberAvatar,
      }))
    : (backendEvent.memberId ? [{
        memberId: backendEvent.memberId,
        memberName: backendEvent.memberName || 'Unknown',
        memberColor: backendEvent.memberColor || '#3b82f6',
        memberAvatar: backendEvent.memberAvatar,
      }] : []);

  // Parse startTime and endTime - handle both date-only and full ISO formats
  // Date-only format (YYYY-MM-DD) for all-day events and important dates: no 'T' in the string
  // Full ISO format for timed events: contains 'T' (e.g., "2024-01-15T19:00:00.000Z")
  const isStartDateOnly = typeof backendEvent.startTime === 'string' && !backendEvent.startTime.includes('T');
  const isEndDateOnly = typeof backendEvent.endTime === 'string' && !backendEvent.endTime.includes('T');
  
  let startTime: Date;
  let endTime: Date;
  
  // Date-only strings need explicit local construction; `new Date("YYYY-MM-DD")`
  // is interpreted as UTC in modern browsers and can shift a day in negative offsets.
  const parseDateOnlyToLocal = (dateStr: string, isEndTime: boolean = false): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    // For end times of date-only events, set to end of day (23:59:59)
    // For start times, set to beginning of day (00:00:00)
    if (isEndTime) {
      return new Date(year, month - 1, day, 23, 59, 59);
    }
    return new Date(year, month - 1, day, 0, 0, 0);
  };

  if (isStartDateOnly) {
    // For date-only strings, create Date at local midnight to avoid UTC shift
    startTime = parseDateOnlyToLocal(backendEvent.startTime, false);
  } else {
    // For full ISO strings, parse UTC and convert to local timezone
    startTime = new Date(backendEvent.startTime);
  }
  
  if (isEndDateOnly) {
    // For date-only strings, create Date at end of day (23:59:59) to avoid UTC shift
    endTime = parseDateOnlyToLocal(backendEvent.endTime, true);
  } else {
    // For full ISO strings, parse UTC and convert to local timezone
    endTime = new Date(backendEvent.endTime);
  }

  return {
    id: backendEvent.id,
    title: backendEvent.title,
    description: backendEvent.description,
    startTime,
    endTime,
    // Legacy fields for backward compatibility
    memberId: backendEvent.memberId || members[0]?.memberId,
    memberName: backendEvent.memberName || members[0]?.memberName,
    memberColor: backendEvent.memberColor || members[0]?.memberColor,
    memberAvatar: backendEvent.memberAvatar || members[0]?.memberAvatar,
    // New: Multiple members support
    memberIds: backendEvent.memberIds || (backendEvent.memberId ? [backendEvent.memberId] : []),
    members,
    location: backendEvent.location,
    category: backendEvent.category,
    isRecurring: backendEvent.isRecurring,
    recurringPattern: backendEvent.recurringPattern,
    dateType: backendEvent.dateType,
    priority: backendEvent.priority,
    reminderDays: backendEvent.reminderDays,
    isImportantDate: backendEvent.isImportantDate,
    isAllDay: backendEvent.isAllDay,
    actorType: backendEvent.actorType,
    actorId: backendEvent.actorId,
  };
};

// Helper function to merge events, deduplicating by ID
// Also removes original recurring events if expanded instances exist
// For recurring event instances, also deduplicate by base event ID + start time
// to handle cases where the same occurrence might have different instance IDs
const mergeEvents = (existingEvents: CalendarEvent[], newEvents: CalendarEvent[]): CalendarEvent[] => {
  const eventMap = new Map<string, CalendarEvent>();
  // For recurring instances, track by base ID + start time to catch duplicates with different instance IDs
  const recurringInstanceKeyMap = new Map<string, string>(); // key: "baseId_startTimeISO", value: instanceId
  
  // Track which original recurring event IDs have expanded instances
  const hasExpandedInstances = new Set<string>();
  [...existingEvents, ...newEvents].forEach(event => {
    if (event.id.includes('_')) {
      // This is an expanded instance - track the original ID
      const originalId = event.id.split('_')[0];
      hasExpandedInstances.add(originalId);
    }
  });
  
  // Add existing events to map, but skip original recurring events that have instances
  existingEvents.forEach(event => {
    if (event.isRecurring && !event.id.includes('_') && hasExpandedInstances.has(event.id)) {
      // Skip original recurring event if instances exist
      return;
    }
    
    // For recurring instances, also track by base ID + start time
    if (event.isRecurring && event.id.includes('_')) {
      const baseId = event.id.split('_')[0];
      const startTimeISO = event.startTime.toISOString();
      const instanceKey = `${baseId}_${startTimeISO}`;
      recurringInstanceKeyMap.set(instanceKey, event.id);
    }
    
    eventMap.set(event.id, event);
  });
  
  // Add/update with new events (new events take precedence for same ID)
  // Also skip original recurring events that have instances
  newEvents.forEach(event => {
    if (event.isRecurring && !event.id.includes('_') && hasExpandedInstances.has(event.id)) {
      // Skip original recurring event if instances exist
      return;
    }
    
    // For recurring instances, check if we already have this occurrence (same base ID + start time)
    // even if the instance ID is different (e.g., backend vs frontend expansion)
    if (event.isRecurring && event.id.includes('_')) {
      const baseId = event.id.split('_')[0];
      const startTimeISO = event.startTime.toISOString();
      const instanceKey = `${baseId}_${startTimeISO}`;
      
      // If we already have this occurrence with a different instance ID, skip it
      if (recurringInstanceKeyMap.has(instanceKey)) {
        const existingInstanceId = recurringInstanceKeyMap.get(instanceKey);
        if (existingInstanceId !== event.id) {
          // This is a duplicate occurrence with a different instance ID - skip it
          return;
        }
      } else {
        // Track this new instance
        recurringInstanceKeyMap.set(instanceKey, event.id);
      }
    }
    
    eventMap.set(event.id, event);
  });
  
  return Array.from(eventMap.values());
};

// Helper function to check if a date range is already covered (currently unused but kept for future optimization)
// const isRangeCovered = (startDate: string, endDate: string, fetchedRanges: FetchedDateRange[]): boolean => {
//   const requestedStart = new Date(startDate + 'T00:00:00').getTime();
//   const requestedEnd = new Date(endDate + 'T23:59:59.999').getTime();
//   
//   return fetchedRanges.some(range => {
//     const rangeStart = new Date(range.startDate + 'T00:00:00').getTime();
//     const rangeEnd = new Date(range.endDate + 'T23:59:59.999').getTime();
//     // Check if requested range is fully contained within an existing range
//     return requestedStart >= rangeStart && requestedEnd <= rangeEnd;
//   });
// };

export const useEventStore = create<EventState>((set, get) => ({
  events: [],
  fetchedRanges: [],
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: null,
  error: null,

  fetchEvents: async (startDate?: string, endDate?: string) => {
    set({ isLoading: true, error: null });
    try {
      let url = `${API_URL}/events`;
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch events' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      let calendarEvents = (data.events || []).map(convertBackendEventToCalendarEvent);
      
      // If date range was provided but backend didn't expand recurring events, do it client-side
      if (startDate && endDate) {
        const { expandRecurringEvent } = await import('../utils/recurrence');
        const expandedEvents: CalendarEvent[] = [];
        const rangeStart = new Date(startDate + 'T00:00:00');
        const rangeEnd = new Date(endDate + 'T23:59:59.999');
        
        // Track which original event IDs have been expanded (by backend or client)
        const expandedOriginalIds = new Set<string>();
        
        // First pass: collect all events, identifying expanded instances
        // Track which original event IDs have been expanded by backend (by checking for instance IDs)
        const backendExpandedIds = new Set<string>();
        const seenInstanceIds = new Set<string>(); // Track all instance IDs to prevent duplicates
        
        for (const event of calendarEvents) {
          if (event.id.includes('_')) {
            // This is an expanded instance from backend
            const originalId = event.id.split('_')[0];
            backendExpandedIds.add(originalId);
            expandedOriginalIds.add(originalId);
            
            // Only add if we haven't seen this exact instance ID before (deduplication)
            if (!seenInstanceIds.has(event.id)) {
              seenInstanceIds.add(event.id);
              expandedEvents.push(event);
            }
          }
        }
        
        // Second pass: handle original events (not yet expanded instances)
        for (const event of calendarEvents) {
          if (!event.id.includes('_')) {
            // This is an original event (not an expanded instance)
            if (event.isRecurring) {
              // Check if backend already expanded this
              if (backendExpandedIds.has(event.id)) {
                // Backend already expanded it - skip the original event
                // Instances were already added in first pass
                continue;
              } else {
                // Backend didn't expand it, do it client-side
                const expanded = expandRecurringEvent(event, rangeStart, rangeEnd);
                if (expanded.length > 0) {
                  // Only add instances that we haven't seen before (in case backend also returned some)
                  for (const instance of expanded) {
                    if (!seenInstanceIds.has(instance.id)) {
                      seenInstanceIds.add(instance.id);
                      expandedEvents.push(instance);
                    }
                  }
                  expandedOriginalIds.add(event.id);
                } else {
                  // Expansion returned empty - event might be outside range, but check if original overlaps
                  const eventStart = new Date(event.startTime);
                  const eventEnd = new Date(event.endTime);
                  if (eventStart <= rangeEnd && eventEnd >= rangeStart) {
                    // Original event overlaps range, include it
                    expandedEvents.push(event);
                  }
                }
              }
            } else {
              // Non-recurring event - check if it overlaps the range
              const eventStart = new Date(event.startTime);
              const eventEnd = new Date(event.endTime);
              if (eventStart <= rangeEnd && eventEnd >= rangeStart) {
                expandedEvents.push(event);
              }
            }
          }
        }
        
        calendarEvents = expandedEvents;
      }
      
      const stateBeforeMerge = get();
      console.log('[eventStore] Fetched events:', {
        startDate,
        endDate,
        newEventsCount: calendarEvents.length,
        existingEventsCount: stateBeforeMerge.events.length,
        recurringCount: calendarEvents.filter((e: CalendarEvent) => e.isRecurring).length,
        expandedInstancesCount: calendarEvents.filter((e: CalendarEvent) => e.id.includes('_')).length,
      });
      
      // MERGE events instead of replacing
      set((currentState) => {
        const mergedEvents = mergeEvents(currentState.events, calendarEvents);
        
        console.log('[eventStore] Merged events:', {
          before: currentState.events.length,
          new: calendarEvents.length,
          after: mergedEvents.length,
          duplicatesRemoved: currentState.events.length + calendarEvents.length - mergedEvents.length,
        });
        
        // Update fetched ranges
        const newRanges = [...currentState.fetchedRanges];
        if (startDate && endDate) {
          // Check if this range is already tracked
          const existingRangeIndex = newRanges.findIndex(
            r => r.startDate === startDate && r.endDate === endDate
          );
          
          if (existingRangeIndex >= 0) {
            // Update timestamp
            newRanges[existingRangeIndex] = {
              startDate,
              endDate,
              timestamp: Date.now(),
            };
          } else {
            // Add new range
            newRanges.push({
              startDate,
              endDate,
              timestamp: Date.now(),
            });
          }
          
          // Clean up old ranges (older than 24 hours) to prevent memory bloat
          // Extended from 1 hour to 24 hours to prevent duplicate fetches when screen stays on
          const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
          const filteredRanges = newRanges.filter(r => r.timestamp > twentyFourHoursAgo);
          
          return {
            events: mergedEvents,
            fetchedRanges: filteredRanges,
            isLoading: false,
          };
        }
        
        return {
          events: mergedEvents,
          isLoading: false,
        };
      });
    } catch (error: any) {
      console.error('Error fetching events:', error);
      set({ error: error.message || 'Failed to fetch events', isLoading: false });
    }
  },

  clearEvents: () => {
    set({ events: [], fetchedRanges: [] });
  },

  createEvent: async (eventData) => {
    set({ isCreating: true, error: null });
    const optimisticId = `temp-${Date.now()}`;
    try {
      const { useFamilyStore } = await import('./familyStore');
      const family = useFamilyStore.getState().family;
      
      if (!family) {
        throw new Error('No family found. Please create or join a family first.');
      }
      const optimisticMembers = eventData.memberIds
        .map((memberId) => family.members.find((member) => member.userId === memberId))
        .filter((member): member is NonNullable<typeof member> => Boolean(member))
        .map((member) => ({
          memberId: member.userId,
          memberName: member.name,
          memberColor: member.color,
          memberAvatar: member.avatar,
        }));
      const primaryMember = optimisticMembers[0];
      const optimisticEvent: CalendarEvent = {
        id: optimisticId,
        title: eventData.title,
        description: eventData.description || '',
        startTime: eventData.startTime,
        endTime: eventData.endTime,
        memberId: primaryMember?.memberId,
        memberName: primaryMember?.memberName,
        memberColor: primaryMember?.memberColor,
        memberAvatar: primaryMember?.memberAvatar,
        memberIds: eventData.memberIds,
        members: optimisticMembers,
        location: eventData.location || '',
        category: eventData.category || '',
        isRecurring: eventData.isRecurring,
        recurringPattern: eventData.recurringPattern,
        dateType: eventData.dateType,
        priority: eventData.priority,
        reminderDays: eventData.reminderDays,
        isImportantDate: eventData.isImportantDate,
        isAllDay: eventData.isAllDay,
      };

      set((state) => ({
        events: [...state.events, optimisticEvent],
      }));

      // Convert Date objects to ISO 8601 UTC strings for storage
      // eventData.startTime/endTime are Date objects in local timezone
      // toISOString() converts to UTC for storage in database
      const requestBody: any = {
        title: eventData.title,
        description: eventData.description || '',
        startTime: eventData.startTime.toISOString(),
        endTime: eventData.endTime.toISOString(),
        memberIds: eventData.memberIds,
        location: eventData.location || '',
        category: eventData.category || '',
      };

      // Add recurring pattern if provided
      if (eventData.isRecurring !== undefined) {
        requestBody.isRecurring = eventData.isRecurring;
      }
      if (eventData.recurringPattern !== undefined) {
        requestBody.recurringPattern = eventData.recurringPattern;
      }

      // Add important dates fields if provided
      if (eventData.dateType !== undefined) {
        requestBody.dateType = eventData.dateType;
      }
      if (eventData.priority !== undefined) {
        requestBody.priority = eventData.priority;
      }
      if (eventData.reminderDays !== undefined) {
        requestBody.reminderDays = eventData.reminderDays;
      }
      if (eventData.isImportantDate !== undefined) {
        requestBody.isImportantDate = eventData.isImportantDate;
      }
      if (eventData.isAllDay !== undefined) {
        requestBody.isAllDay = eventData.isAllDay;
      }

      const response = await fetch(`${API_URL}/events`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to create event' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const newEvent = convertBackendEventToCalendarEvent(data.event);
      
      set((state) => {
        const hasOptimistic = state.events.some((event) => event.id === optimisticId);
        return {
          events: hasOptimistic
            ? state.events.map((event) => (event.id === optimisticId ? newEvent : event))
            : [...state.events, newEvent],
          isCreating: false,
        };
      });

      return newEvent;
    } catch (error: any) {
      console.error('Error creating event:', error);
      set((state) => ({
        events: state.events.filter((event) => event.id !== optimisticId),
        error: error.message || 'Failed to create event',
        isCreating: false,
      }));
      throw error;
    }
  },

  updateEvent: async (eventId, updates) => {
    set({ isUpdating: true, error: null });
    const baseEventId = getBaseEventId(eventId);
    const originalEvents = get().events.filter(
      (event) => event.id === baseEventId || event.id.startsWith(baseEventId + '_')
    );
    try {
      const { useFamilyStore } = await import('./familyStore');
      const family = useFamilyStore.getState().family;
      
      if (!family) {
        throw new Error('No family found. Please create or join a family first.');
      }

      const memberPatch =
        updates.memberIds !== undefined
          ? (() => {
              const optimisticMembers = updates.memberIds
                .map((memberId) => family.members.find((member) => member.userId === memberId))
                .filter((member): member is NonNullable<typeof member> => Boolean(member))
                .map((member) => ({
                  memberId: member.userId,
                  memberName: member.name,
                  memberColor: member.color,
                  memberAvatar: member.avatar,
                }));
              const primaryMember = optimisticMembers[0];
              return {
                memberIds: updates.memberIds,
                members: optimisticMembers,
                memberId: primaryMember?.memberId,
                memberName: primaryMember?.memberName,
                memberColor: primaryMember?.memberColor,
                memberAvatar: primaryMember?.memberAvatar,
              };
            })()
          : {};

      const fieldPatch: Partial<CalendarEvent> = {
        ...(updates.title !== undefined ? { title: updates.title } : {}),
        ...(updates.description !== undefined ? { description: updates.description } : {}),
        ...(updates.location !== undefined ? { location: updates.location } : {}),
        ...(updates.category !== undefined ? { category: updates.category } : {}),
        ...(updates.isRecurring !== undefined ? { isRecurring: updates.isRecurring } : {}),
        ...(updates.recurringPattern !== undefined ? { recurringPattern: updates.recurringPattern } : {}),
        ...(updates.dateType !== undefined ? { dateType: updates.dateType } : {}),
        ...(updates.priority !== undefined ? { priority: updates.priority } : {}),
        ...(updates.reminderDays !== undefined ? { reminderDays: updates.reminderDays } : {}),
        ...(updates.isImportantDate !== undefined ? { isImportantDate: updates.isImportantDate } : {}),
        ...(updates.isAllDay !== undefined ? { isAllDay: updates.isAllDay } : {}),
        ...memberPatch,
      };

      set((state) => ({
        events: state.events.map((event) => {
          if (event.id === baseEventId || event.id.startsWith(baseEventId + '_')) {
            const isInstance = event.id !== baseEventId && event.id.startsWith(baseEventId + '_');
            const merged = {
              ...event,
              ...fieldPatch,
            };
            if (!isInstance) {
              if (updates.startTime !== undefined) {
                merged.startTime = updates.startTime;
              }
              if (updates.endTime !== undefined) {
                merged.endTime = updates.endTime;
              }
            }
            return merged;
          }
          return event;
        }),
      }));

      const requestBody: any = {};
      
      if (updates.title !== undefined) requestBody.title = updates.title;
      if (updates.description !== undefined) requestBody.description = updates.description;
      // Convert Date objects to ISO 8601 UTC strings for storage (local timezone -> UTC)
      if (updates.startTime !== undefined) requestBody.startTime = updates.startTime.toISOString();
      if (updates.endTime !== undefined) requestBody.endTime = updates.endTime.toISOString();
      if (updates.location !== undefined) requestBody.location = updates.location;
      if (updates.category !== undefined) requestBody.category = updates.category;
      if (updates.isRecurring !== undefined) requestBody.isRecurring = updates.isRecurring;
      if (updates.recurringPattern !== undefined) requestBody.recurringPattern = updates.recurringPattern;
      if (updates.dateType !== undefined) requestBody.dateType = updates.dateType;
      if (updates.priority !== undefined) requestBody.priority = updates.priority;
      if (updates.reminderDays !== undefined) requestBody.reminderDays = updates.reminderDays;
      if (updates.isImportantDate !== undefined) requestBody.isImportantDate = updates.isImportantDate;
      if (updates.isAllDay !== undefined) requestBody.isAllDay = updates.isAllDay;
      if (updates.updateAllInstances !== undefined) requestBody.updateAllInstances = updates.updateAllInstances;

      // Handle member updates - only send memberIds, backend will look up member data
      if (updates.memberIds !== undefined) {
        requestBody.memberIds = updates.memberIds;
      }

      const response = await fetch(`${API_URL}/events/${baseEventId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to update event' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const updatedEvent = convertBackendEventToCalendarEvent(data.event);
      
      const mergeUpdatesIntoEvent = (existing: CalendarEvent): CalendarEvent => {
        const isInstance = existing.id !== baseEventId && existing.id.startsWith(baseEventId + '_');

        // Apply server-returned fields to all visible instances, but keep occurrence
        // start/end times for expanded instances.
        const merged: CalendarEvent = {
          ...existing,
          title: updatedEvent.title,
          description: updatedEvent.description,
          location: updatedEvent.location,
          category: updatedEvent.category,
          isRecurring: updatedEvent.isRecurring,
          recurringPattern: updatedEvent.recurringPattern,
          dateType: updatedEvent.dateType,
          priority: updatedEvent.priority,
          reminderDays: updatedEvent.reminderDays,
          isImportantDate: updatedEvent.isImportantDate,
          isAllDay: updatedEvent.isAllDay,
          memberId: updatedEvent.memberId,
          memberName: updatedEvent.memberName,
          memberColor: updatedEvent.memberColor,
          memberAvatar: updatedEvent.memberAvatar,
          memberIds: updatedEvent.memberIds,
          members: updatedEvent.members,
        };

        if (!isInstance) {
          // If the base event is present in state, replace fully (including start/end).
          merged.startTime = updatedEvent.startTime;
          merged.endTime = updatedEvent.endTime;
          merged.id = baseEventId;
        }

        return merged;
      };

      set((state) => ({
        events: state.events.map((e) => {
          if (e.id === baseEventId || e.id.startsWith(baseEventId + '_')) {
            return mergeUpdatesIntoEvent(e);
          }
          return e;
        }),
        isUpdating: false,
      }));
    } catch (error: any) {
      console.error('Error updating event:', error);
      set((state) => {
        const restoredEvents = state.events.map((event) => {
          const original = originalEvents.find((item) => item.id === event.id);
          return original ?? event;
        });
        return {
          events: restoredEvents,
          error: error.message || 'Failed to update event',
          isUpdating: false,
        };
      });
      throw error;
    }
  },

  deleteEvent: async (eventId, instanceOnly = false) => {
    const baseEventId = getBaseEventId(eventId);
    const shouldRemove = (event: CalendarEvent) =>
      instanceOnly ? event.id === eventId : event.id === baseEventId || event.id.startsWith(baseEventId + '_');
    const removedEvents = get().events.filter(shouldRemove);

    set((state) => ({
      events: state.events.filter((event) => !shouldRemove(event)),
      isDeleting: eventId,
      error: null,
    }));
    try {
      let url = `${API_URL}/events/${baseEventId}`;
      if (instanceOnly) {
        url += '?instanceOnly=true';
      }
      const response = await fetch(url, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to delete event' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      set({ isDeleting: null });
    } catch (error: any) {
      console.error('Error deleting event:', error);
      set((state) => {
        const existingIds = new Set(state.events.map((event) => event.id));
        const restored = removedEvents.filter((event) => !existingIds.has(event.id));
        return {
          events: [...state.events, ...restored],
          error: error.message || 'Failed to delete event',
          isDeleting: null,
        };
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),

  // Important dates filters and queries
  getImportantDates: (): CalendarEvent[] => {
    const state = useEventStore.getState();
    return state.events.filter((event: CalendarEvent) => event.isImportantDate === true);
  },

  getByDateType: (dateType: DateType): CalendarEvent[] => {
    const state = useEventStore.getState();
    return state.events.filter((event: CalendarEvent) => event.dateType === dateType);
  },

  getByPriority: (priority: Priority): CalendarEvent[] => {
    const state = useEventStore.getState();
    return state.events.filter((event: CalendarEvent) => event.priority === priority);
  },

  getUpcomingImportantDates: (days: number): CalendarEvent[] => {
    const state = useEventStore.getState();
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(now.getDate() + days);

    return state.events.filter((event: CalendarEvent) => {
      if (!event.isImportantDate) return false;
      const eventDate = new Date(event.startTime);
      return eventDate >= now && eventDate <= futureDate;
    }).sort((a: CalendarEvent, b: CalendarEvent) => {
      const dateA = new Date(a.startTime);
      const dateB = new Date(b.startTime);
      return dateA.getTime() - dateB.getTime();
    });
  },
}));

